import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import accountingService, { FiscalYearDto } from '../services/accountingService';
import { authService } from '../services/authService';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';

interface FiscalYearContextType {
  fiscalYears: FiscalYearDto[];
  currentFiscalYear: FiscalYearDto | null;
  isLoading: boolean;
  setCurrentFiscalYear: (id: number) => Promise<void>;
  refresh: () => Promise<void>;
}

const FiscalYearContext = createContext<FiscalYearContextType | undefined>(undefined);

// انتخاب پیش‌فرض: آخرین سال مالی کاربر → سال با فلگ جاری → سال شامل امروز → آخرین سال
const pickDefaultFiscalYear = (years: FiscalYearDto[], lastFiscalYearId?: number | null): FiscalYearDto | undefined => {
  if (lastFiscalYearId) {
    const last = years.find(y => y.id === lastFiscalYearId);
    if (last) return last;
  }
  const marked = years.find(y => y.isCurrent);
  if (marked) return marked;
  const today = new Date().toISOString().slice(0, 10);
  return (
    years.find(y => y.startDate <= today && y.endDate >= today) ??
    [...years].sort((a, b) => b.endDate.localeCompare(a.endDate))[0]
  );
};

export const FiscalYearProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, updateUser } = useAuth();
  const { showError } = useNotification();
  const [fiscalYears, setFiscalYears] = useState<FiscalYearDto[]>([]);
  const [currentFiscalYear, setCurrentFiscalYearState] = useState<FiscalYearDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await accountingService.getAllFiscalYears();
      setFiscalYears(response.data);
      setCurrentFiscalYearState(prev => {
        // اگر انتخاب فعلی هنوز معتبر است همان را نگه دار، وگرنه پیش‌فرض را انتخاب کن
        const stillValid = prev && response.data.some(y => y.id === prev.id);
        return stillValid ? response.data.find(y => y.id === prev!.id)! : pickDefaultFiscalYear(response.data, user?.lastFiscalYearId) ?? null;
      });
    } catch (err: any) {
      // کاربر ممکن است به ماژول حسابداری دسترسی نداشته باشد — بی‌صدا رد شو
      setFiscalYears([]);
      setCurrentFiscalYearState(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.lastFiscalYearId]);

  useEffect(() => {
    if (user) {
      load();
    } else {
      setFiscalYears([]);
      setCurrentFiscalYearState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setCurrentFiscalYear = async (id: number) => {
    const target = fiscalYears.find(y => y.id === id);
    if (!target || target.id === currentFiscalYear?.id) return;
    setCurrentFiscalYearState(target);
    updateUser({ lastFiscalYearId: id });
    try {
      await authService.setLastFiscalYear(id);
    } catch (err: any) {
      showError(err?.response?.data?.message || 'خطا در ذخیره سال مالی کاربر');
    }
  };

  return (
    <FiscalYearContext.Provider
      value={{
        fiscalYears,
        currentFiscalYear,
        isLoading,
        setCurrentFiscalYear,
        refresh: load,
      }}
    >
      {children}
    </FiscalYearContext.Provider>
  );
};

export const useFiscalYear = () => {
  const context = useContext(FiscalYearContext);
  if (context === undefined) {
    throw new Error('useFiscalYear must be used within a FiscalYearProvider');
  }
  return context;
};
